import { create } from 'zustand'
import type { ShipTier } from '@/ships/shipTypes'
import { SHIP_TIERS } from '@/ships/shipTypes'

export interface OwnedShip {
  id: string
  tier: ShipTier
  health: number
  maxHealth: number
  dockedAt: number // dock index, -1 if sailing
  theta: number
  phi: number
  heading: number // yaw on surface
}

interface ShipState {
  ownedShips: OwnedShip[]
  activeShipId: string | null
  aboard: boolean

  // Cannon cooldowns (seconds remaining)
  portCooldown: number
  starboardCooldown: number

  // Actions
  buyShip: (tier: ShipTier, dockIndex: number, theta: number, phi: number, heading: number) => string
  boardShip: (shipId: string) => void
  disembark: (dockIndex: number) => void
  updateShipPosition: (theta: number, phi: number, heading: number) => void
  setPortCooldown: (cd: number) => void
  setStarboardCooldown: (cd: number) => void
  damageShip: (shipId: string, damage: number) => void
  getActiveShip: () => OwnedShip | null
}

let _nextId = 1

export const useShipStore = create<ShipState>((set, get) => ({
  ownedShips: [],
  activeShipId: null,
  aboard: false,
  portCooldown: 0,
  starboardCooldown: 0,

  buyShip: (tier, dockIndex, theta, phi, heading) => {
    const info = SHIP_TIERS[tier]
    const id = `ship_${_nextId++}`
    const ship: OwnedShip = {
      id,
      tier,
      health: info.maxHealth,
      maxHealth: info.maxHealth,
      dockedAt: dockIndex,
      theta,
      phi,
      heading,
    }
    set((s) => ({ ownedShips: [...s.ownedShips, ship] }))
    return id
  },

  boardShip: (shipId) => {
    const ship = get().ownedShips.find((s) => s.id === shipId)
    if (!ship) return
    set({ activeShipId: shipId, aboard: true })
  },

  disembark: (dockIndex) => {
    const { activeShipId, ownedShips } = get()
    if (!activeShipId) return
    set({
      activeShipId: null,
      aboard: false,
      portCooldown: 0,
      starboardCooldown: 0,
      ownedShips: ownedShips.map((s) =>
        s.id === activeShipId ? { ...s, dockedAt: dockIndex } : s
      ),
    })
  },

  updateShipPosition: (theta, phi, heading) => {
    const { activeShipId, ownedShips } = get()
    if (!activeShipId) return
    set({
      ownedShips: ownedShips.map((s) =>
        s.id === activeShipId ? { ...s, theta, phi, heading, dockedAt: -1 } : s
      ),
    })
  },

  setPortCooldown: (cd) => set({ portCooldown: cd }),
  setStarboardCooldown: (cd) => set({ starboardCooldown: cd }),

  damageShip: (shipId, damage) => {
    set((s) => ({
      ownedShips: s.ownedShips.map((ship) =>
        ship.id === shipId
          ? { ...ship, health: Math.max(0, ship.health - damage) }
          : ship
      ),
    }))
  },

  getActiveShip: () => {
    const { activeShipId, ownedShips } = get()
    if (!activeShipId) return null
    return ownedShips.find((s) => s.id === activeShipId) ?? null
  },
}))
